import { useEffect, useMemo, useState } from 'react';
import {
  DraftVisit,
  ExistingVisit,
  UploadVisitType,
} from 'src/store-redux/siteUpload/siteUploadStore.types';
import { useParams } from 'react-router-dom';
import { CohortType } from '@dynamika-shared/types/cohorts';
import {
  filterAndSortVisits,
  getFirstVisit,
  getSortedVisits,
} from '@components/SiteUserUpload/helpers/visitHelpers';
import {
  StepComponentProps,
  VisitDefinitionsStatus,
} from '@components/SiteUserUpload/types/uploadTypes';
import { useSiteSubjectsListSlice } from 'src/store-redux/SiteSubjectsList/SiteSubjectsList.slice';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';

type UseVisitStepHookArgs = Pick<
  StepComponentProps,
  'setDirtyDrawerState' | 'data' | 'subject'
>;
export const useVisitStepHook = ({
  setDirtyDrawerState,
  data,
  subject,
}: UseVisitStepHookArgs) => {
  const { id: clinicalTrialId, siteId } = useParams<{
    id: string;
    siteId: string;
  }>();

  const createVisit = useSiteUploadSlice((_, actions) => actions.createVisit);

  const [selectedVisit, setSelectedVisit] = useState<
    ExistingVisit | DraftVisit | undefined
  >(undefined);
  const [cohort, setCohort] = useState<CohortType>();
  const [selectOpen, setSelectOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState<
    UploadVisitType.EARLY_TERMINATED | UploadVisitType.UNSCHEDULED | null
  >(null);

  const [{ subjectDetails }, { fetchSiteSubjectDetails }] =
    useSiteSubjectsListSlice((s, a) => [s, a]);

  useEffect(() => {
    if (selectedVisit) {
      setDirtyDrawerState(true);
    }
  }, [selectedVisit, setDirtyDrawerState]);

  const flatData = useMemo(() => {
    return data?.configuration?.cohorts.reduce(
      (acc: Record<string, CohortType>, cohort: CohortType) => {
        if (cohort.id) {
          acc[cohort.id] = cohort;
        }
        return acc;
      },
      {} as Record<string, CohortType>
    );
  }, [data?.configuration?.cohorts]);

  useEffect(() => {
    if (clinicalTrialId && siteId && subject?.id) {
      fetchSiteSubjectDetails({
        clinicalTrialId: clinicalTrialId,
        patientId: subject?.id,
        siteId: siteId,
      });
    }
  }, [clinicalTrialId, siteId, subject?.id]);

  useEffect(() => {
    const matchedCohort = flatData?.[subject?.cohortId ?? ''];
    setCohort(matchedCohort);
    const sortedVisits = getSortedVisits(matchedCohort, subjectDetails?.visits);
    if (sortedVisits?.length) {
      const createdVisits = filterAndSortVisits(
        sortedVisits,
        VisitDefinitionsStatus.CREATED
      );

      // Check if there are any visits with 'CREATED' status
      if (createdVisits.length > 0) {
        const expectedVisit = getFirstVisit(createdVisits);
        setSelectedVisit(expectedVisit);
      } else {
        // If there are no visits with 'CREATED' status, select the visit with the highest baseline among 'IN_PROGRESS' visits
        const inProgressVisits = filterAndSortVisits(
          sortedVisits,
          VisitDefinitionsStatus.IN_PROGRESS
        );
        const selectedVisit = getFirstVisit(inProgressVisits);
        setSelectedVisit(selectedVisit);
      }
    }
  }, [flatData, subject?.cohortId, subjectDetails?.visits]);

  return {
    clinicalTrialId,
    siteId,
    createVisit,
    selectedVisit,
    setSelectedVisit,
    selectOpen,
    setSelectOpen,
    flatData,
    modalOpen,
    setModalOpen,
    cohort,
    subjectDetails,
  };
};
